import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import { AbsenceApiService } from '@/rennovation/common/services/api/absenceApiService';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import { ApiAbsence } from '@/rennovation/planning-view/types/api/absence-responses';
import { usePlanningBridgeStore } from '@/rennovation/planning-view/store/bridge/planningBridge';
import { useI18n } from 'vue-i18n';

interface ContextMenuState {
	isOpen: boolean;
	x: number;
	y: number;
	driverId: string | null;
	driverName: string;
	date: string | null;
}

export type AbsenceModalMode = 'create' | 'edit';

interface AbsenceFormModel {
	id?: string;
	user: string | null;
	driverName: string;
	entireDay: boolean;
	type: string | null;
	status: string;
	startDate: string;
	startTime: string | null;
	endDate: string;
	endTime: string | null;
	priority: number;
	files: any[];
	isOverlappingAbsence: boolean;
	overlapText: string;
}

interface EditModalState {
	isOpen: boolean;
	mode: AbsenceModalMode;
	absenceId: string | null;
	originalStatus: string | null;
	formModel: AbsenceFormModel;
}

const absenceApi = new AbsenceApiService();

const buildDefaultAbsence = (): AbsenceFormModel => ({
	user: null,
	driverName: '',
	entireDay: true,
	type: null,
	status: 'pending',
	startDate: new Date().toISOString().split('T')[0],
	startTime: null,
	endDate: new Date().toISOString().split('T')[0],
	endTime: null,
	priority: 1,
	files: [],
	isOverlappingAbsence: false,
	overlapText: '',
});

export const usePlanningAbsenceMenuStore = defineStore('planningAbsenceMenu', () => {
	const { t } = useI18n();
	const alertStore = useAlertStore();
	const planningBridge = usePlanningBridgeStore();

	const contextMenu = ref<ContextMenuState>({
		isOpen: false,
		x: 0,
		y: 0,
		driverId: null,
		driverName: '',
		date: null,
	});

	const absenceList = ref<{ isLoading: boolean; items: ApiAbsence[] }>({
		isLoading: false,
		items: [],
	});

	const infoModal = ref<{ isOpen: boolean; absenceId: string | null }>({
		isOpen: false,
		absenceId: null,
	});

	const editModal = ref<EditModalState>({
		isOpen: false,
		mode: 'create',
		absenceId: null,
		originalStatus: null,
		formModel: buildDefaultAbsence(),
	});

	const fetchAbsencesForCell = async (driverId: string, date: string): Promise<void> => {
		absenceList.value.isLoading = true;
		try {
			const items = await absenceApi.find(date, date, driverId);
			absenceList.value.items = items;
		} catch (err) {
			absenceList.value.items = [];
			console.error('Failed to fetch absences for cell:', err);
			const backendMessage = err?.response?.data?.message;
			alertStore.showAlert(backendMessage ?? t('absence.fetch_failed'), 'error');
		} finally {
			absenceList.value.isLoading = false;
		}
	};

	const refetch = async (driverId: string, date: string): Promise<void> => {
		await fetchAbsencesForCell(driverId, date);
	};

	const openContextMenu = async (payload: { x: number; y: number; driverId: string; driverName: string; date: string }): Promise<void> => {
		contextMenu.value = { isOpen: true, ...payload };
		await fetchAbsencesForCell(payload.driverId, payload.date);
	};

	const closeContextMenu = (): void => {
		contextMenu.value.isOpen = false;
	};

	const openInfoModal = (absenceId: string): void => {
		infoModal.value = { isOpen: true, absenceId };
	};

	const openEditModal = (absenceId: string): void => {
		const existing = absenceList.value.items.find((a) => a.id === absenceId);
		editModal.value = {
			isOpen: true,
			mode: 'edit',
			absenceId,
			originalStatus: existing?.status ?? null,
			formModel: { ...buildDefaultAbsence(), ...cloneDeep(existing), driverName: contextMenu.value.driverName },
		};
	};

	const openEditModalWithAbsence = (absence: ApiAbsence, driverName = ''): void => {
		editModal.value = {
			isOpen: true,
			mode: 'edit',
			absenceId: absence.id,
			originalStatus: absence.status,
			formModel: { ...buildDefaultAbsence(), ...cloneDeep(absence), driverName },
		};
	};

	const openCreateModal = (): void => {
		const fallbackDate = buildDefaultAbsence().startDate;
		editModal.value = {
			isOpen: true,
			mode: 'create',
			absenceId: null,
			originalStatus: null,
			formModel: {
				...buildDefaultAbsence(),
				user: contextMenu.value.driverId,
				driverName: contextMenu.value.driverName,
				startDate: contextMenu.value.date ?? fallbackDate,
				endDate: contextMenu.value.date ?? fallbackDate,
			},
		};
	};

	const closeEditModal = (): void => {
		editModal.value = {
			isOpen: false,
			mode: 'create',
			absenceId: null,
			originalStatus: null,
			formModel: buildDefaultAbsence(),
		};
	};

	const updateFormModel = (val: AbsenceFormModel): void => {
		editModal.value.formModel = val;
	};

	const saveAbsence = async (payload: AbsenceFormModel & { entireDay: boolean }): Promise<void> => {
		try {
			if (editModal.value.mode === 'edit') {
				await planningBridge.updateAbsence(payload);
				alertStore.showAlert(t('absence.update_success'), 'success');
			} else {
				await planningBridge.createAbsence(payload);
				alertStore.showAlert(t('absence.create_success'), 'success');
			}
		} catch (err) {
			console.error('Failed to save absence:', err);
			alertStore.showAlert(t('absence.save_failed'), 'error');
			throw err;
		}
	};

	const deleteConfirm = ref<{ isOpen: boolean; absenceId: string | null; deleting: boolean }>({
		isOpen: false,
		absenceId: null,
		deleting: false,
	});

	const openDeleteConfirm = (absenceId: string): void => {
		deleteConfirm.value = { isOpen: true, absenceId, deleting: false };
	};

	const closeDeleteConfirm = (): void => {
		deleteConfirm.value = { isOpen: false, absenceId: null, deleting: false };
	};

	const confirmDeleteAbsence = async (changeComment: string): Promise<void> => {
		const { absenceId } = deleteConfirm.value;
		if (!absenceId) return;

		deleteConfirm.value.deleting = true;
		try {
			await planningBridge.deleteAbsence({ id: absenceId, changeComment });
			alertStore.showAlert(t('message_saved'), 'success');

			const { driverId, date } = contextMenu.value;
			if (driverId && date) await fetchAbsencesForCell(driverId, date);

			infoModal.value = { isOpen: false, absenceId: null };
			closeDeleteConfirm();
		} catch (err) {
			console.error('Failed to delete absence:', err);
			alertStore.showAlert(t('absence.delete_failed'), 'error');
		} finally {
			deleteConfirm.value.deleting = false;
		}
	};

	const isCellLocked = computed(() => {
		const { date } = contextMenu.value;
		if (!date) return false;
		return planningBridge.isBeforeFirstPastDay(date, null);
	});

	const isAbsenceLocked = (absence: ApiAbsence): boolean => {
		return planningBridge.isBeforeFirstPastDay(absence.startDate, absence.startTime);
	};

	return {
		contextMenu,
		absenceList,
		infoModal,
		editModal,
		deleteConfirm,
		isCellLocked,
		refetch,
		openContextMenu,
		closeContextMenu,
		openInfoModal,
		openEditModal,
		openEditModalWithAbsence,
		openCreateModal,
		closeEditModal,
		updateFormModel,
		saveAbsence,
		openDeleteConfirm,
		closeDeleteConfirm,
		confirmDeleteAbsence,
		isAbsenceLocked,
	};
});
